/**
 * interviewCommunicationQueries.ts
 *
 * React Query hooks for the Communication step's data needs:
 *  - ATS email templates (different categories per communication item)
 *  - User options for From / CC / BCC dropdowns
 *
 * The Communication step has 5 editor blocks. Each block will eventually
 * fetch templates from its own set of categories. For now most share the
 * same defaults — update the consts below when PM confirms per-block splits.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SelectOption } from '@kula-ai/components';
import {
  ATS_COMM_TEMPLATE_CHANNEL_EMAIL,
  fetchAtsCommTemplates,
  mapAtsTemplatesToSelectOptions,
  type AtsCommTemplate,
} from '@/api/atsCommTemplates';
import { useUsersWithScope } from '@/api/useUsersWithScope';

// ---------------------------------------------------------------------------
// Template categories per communication block
// TODO: update each const when PM confirms per-block category splits
// ---------------------------------------------------------------------------

/** Candidate calendar invite */
export const CANDIDATE_CALENDAR_TEMPLATE_CATEGORIES: readonly string[] = [
  'candidate_adhoc',
  'candidate_application_rejected',
  'candidate_offer_notice',
  'candidate_assessment_notice',
  'candidate_job_stage_action_notice',
];

/** Candidate follow-up email */
export const CANDIDATE_EMAIL_TEMPLATE_CATEGORIES: readonly string[] = [
  'candidate_adhoc',
  'candidate_application_rejected',
  'candidate_offer_notice',
  'candidate_assessment_notice',
  'candidate_job_stage_action_notice',
];

/** Interviewer calendar invite */
export const INTERVIEWER_CALENDAR_TEMPLATE_CATEGORIES: readonly string[] = [
  'candidate_adhoc',
  'candidate_application_rejected',
  'candidate_offer_notice',
  'candidate_assessment_notice',
  'candidate_job_stage_action_notice',
];

/** Interviewer follow-up email */
export const INTERVIEWER_EMAIL_TEMPLATE_CATEGORIES: readonly string[] = [
  'candidate_adhoc',
  'candidate_application_rejected',
  'candidate_offer_notice',
  'candidate_assessment_notice',
  'candidate_job_stage_action_notice',
];

/** Self-schedule slot selection email */
export const SELF_SCHEDULE_EMAIL_TEMPLATE_CATEGORIES: readonly string[] = [
  'candidate_adhoc',
  'candidate_application_rejected',
  'candidate_offer_notice',
  'candidate_assessment_notice',
  'candidate_job_stage_action_notice',
];

// ---------------------------------------------------------------------------
// Scope → categories mapping
// ---------------------------------------------------------------------------

/**
 * Scope values passed to `useInterviewEmailTemplateOptions`.
 * Currently the calendar invite and email for each audience share the same
 * scope — split into separate scopes if they need different categories.
 */
export type InterviewTemplateScope =
  | 'candidate'
  | 'interviewer'
  | 'self-schedule';

function getCategoriesForScope(
  scope: InterviewTemplateScope
): readonly string[] {
  switch (scope) {
    case 'candidate':
      return CANDIDATE_CALENDAR_TEMPLATE_CATEGORIES;
    case 'interviewer':
      return INTERVIEWER_CALENDAR_TEMPLATE_CATEGORIES;
    case 'self-schedule':
      return SELF_SCHEDULE_EMAIL_TEMPLATE_CATEGORIES;
  }
}

// ---------------------------------------------------------------------------
// Template options hook
// ---------------------------------------------------------------------------

const templateQueryKeys = {
  all: ['interview-activity', 'communication', 'ats-comm-templates'] as const,
  byScope: (scope: InterviewTemplateScope) =>
    [...templateQueryKeys.all, scope] as const,
};

export type InterviewCommunicationTemplatesFormat = 'selectOptions' | 'none';

export function useInterviewEmailTemplateOptions(
  _scope: InterviewTemplateScope,
  _options?: { format?: 'selectOptions' }
): UseQueryResult<SelectOption[], Error>;

export function useInterviewEmailTemplateOptions(
  _scope: InterviewTemplateScope,
  _options: { format: 'none' }
): UseQueryResult<AtsCommTemplate[], Error>;

export function useInterviewEmailTemplateOptions(
  scope: InterviewTemplateScope,
  options?: { format?: InterviewCommunicationTemplatesFormat }
): UseQueryResult<SelectOption[] | AtsCommTemplate[], Error> {
  const categories = getCategoriesForScope(scope);
  const format = options?.format ?? 'selectOptions';

  return useQuery({
    queryKey: [...templateQueryKeys.byScope(scope), format],
    queryFn: () =>
      fetchAtsCommTemplates({
        channel: ATS_COMM_TEMPLATE_CHANNEL_EMAIL,
        categories,
        items: 200,
        nameContains: '',
      }),
    staleTime: 60_000,
    select:
      format === 'none'
        ? undefined
        : (rows) => mapAtsTemplatesToSelectOptions(rows),
  }) as UseQueryResult<SelectOption[] | AtsCommTemplate[], Error>;
}

// ---------------------------------------------------------------------------
// User options hook (From / CC / BCC)
// All email editors share the same user list — users with email_delegatable scope.
// ---------------------------------------------------------------------------

export function useInterviewCommunicationSenderOptions(): UseQueryResult<
  SelectOption[],
  Error
> {
  return useUsersWithScope('email_delegatable');
}
